import { useState, useRef, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { WaveSine, SpeakerHigh, CircleNotch, Stop } from "@phosphor-icons/react";
import { toast } from "sonner";

// Lets the operator attach an ALREADY-RECORDED voiceover (from generate_voiceover's output, or an
// uploaded recording) without typing its media_id in chat — rides along in the same `attachments`
// array as file uploads, so Shee sees it as an "[ATTACHED VOICEOVER: ...]" context block.
export function VoiceoverPicker({ api, onSelect, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/media", { params: { kind: "audio" } })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Couldn't load saved voiceovers"))
      .finally(() => setLoading(false));
  }, [open, api]);

  const filtered = search.trim()
    ? items.filter((i) => (i.filename || "").toLowerCase().includes(search.trim().toLowerCase()))
    : items;

  const play = (item) => {
    if (!audioRef.current) audioRef.current = new Audio();
    if (playingId === item.id) {
      audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    setPlayingId(item.id);
    audioRef.current.src = item.file_url;
    audioRef.current.onended = () => setPlayingId(null);
    audioRef.current.play().catch(() => setPlayingId(null));
  };

  const choose = (item) => {
    onSelect(item);
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} title="Attach a saved voiceover"
          className="rounded-[12px] h-[52px] w-[52px] p-0 shrink-0 bg-[#eef4f5] hover:bg-[#e0eef0] text-[#3A7A8C] flex items-center justify-center disabled:opacity-50"
          data-testid="cd-voiceover-picker-trigger">
          <WaveSine size={18} weight="bold" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start" side="top" sideOffset={10}>
        <Command shouldFilter={false}>
          <CommandInput value={search} onValueChange={setSearch} placeholder="Search saved voiceovers…" data-testid="cd-voiceover-picker-search" />
          <CommandList>
            {loading ? (
              <div className="p-4 text-xs text-[#7A9090] flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> Loading…</div>
            ) : (
              <>
                <CommandEmpty>No saved voiceovers yet — generate one first via Ask Shee.</CommandEmpty>
                {filtered.map((item) => (
                  <CommandItem key={item.id} value={item.id} onSelect={() => choose(item)}
                    data-testid={`cd-voiceover-picker-option-${item.id}`} className="flex items-center justify-between gap-2 cursor-pointer">
                    <p className="text-sm text-[#3C4048] truncate">{item.filename || "Voiceover"}</p>
                    <button type="button" onClick={(e) => { e.stopPropagation(); play(item); }}
                      className="shrink-0 text-[#3A7A8C] hover:text-[#5FA8B4] p-1" data-testid={`cd-voiceover-picker-preview-${item.id}`}>
                      {playingId === item.id ? <Stop size={14} weight="fill" /> : <SpeakerHigh size={14} weight="regular" />}
                    </button>
                  </CommandItem>
                ))}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
